import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Home.css';
import FormularioGrupo from '../Formulario/FormularioGrupo';
import GrupoDropDown from '../Dropdown/GrupoDropDown';
import GroupsModal from '../Modal/GroupsModal';
import AllGroupsModal from '../Modal/AllGroupsModal';
import FormularioPostagem from '../Formulario/FormularioPostagem';
import { extractPostDate, formatPostDate, formatPostDateMinutes } from '../utils/dateUtils';
import Lightbox from '../components/Lightbox';
import { getAuthToken, getUsuarioSistemaId } from '../utils/auth';
import BASE_URL from '../utils/api';

/*
	Home component layout:
	- Left: sidebar (20%) rgb(235, 133, 0) with company highlight
	- Center: feed + composer (60%) with expandable composer
	- Right: 'Atrasos' container (20%)
*/

function Home() {
	const [posts, setPosts] = useState([]);
	const [postsLoading, setPostsLoading] = useState(false);
	const [postsError, setPostsError] = useState('');
	const [postingGlobal, setPostingGlobal] = useState(false);
	const [editingPostId, setEditingPostId] = useState(null);
	const [editingText, setEditingText] = useState('');
	const [editingSaving, setEditingSaving] = useState(false);
	const [confirmDeletePostId, setConfirmDeletePostId] = useState(null);
	// Edit-mode file management
	const [editImages, setEditImages] = useState([]); // [{id, url, type, name, isNew, file?}]
	const [editFiles, setEditFiles] = useState([]);   // non-image files (PDF, TXT, etc.)
	const [editNewImages, setEditNewImages] = useState([]); // newly added File objects for images
	const [editNewFiles, setEditNewFiles] = useState([]);   // newly added File objects for non-images
	const [editRemovedFileIds, setEditRemovedFileIds] = useState([]); // IDs of existing files to delete
	// "Ver todos" popup state
	const [showVerTodos, setShowVerTodos] = useState(false);
	const [verTodosSelectedImages, setVerTodosSelectedImages] = useState(new Set());
	const [verTodosSelectedFiles, setVerTodosSelectedFiles] = useState(new Set());
	const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
	const editImageInputRef = useRef(null);
	const editFileInputRef = useRef(null);

	// per-post image index for gallery navigation
	const [imageIndexes, setImageIndexes] = useState({});
	const postObjectUrlRefs = useRef({});

	// Lightbox state for fullscreen preview of the post's main image
	const [lightboxOpen, setLightboxOpen] = useState(false);
	const [lbImages, setLbImages] = useState([]);
	const [lbIndex, setLbIndex] = useState(0);

	const openLightbox = (images, idx = 0) => {
		setLbImages(images || []);
		setLbIndex(typeof idx === 'number' ? idx : 0);
		setLightboxOpen(true);
	};
	const closeLightbox = () => setLightboxOpen(false);
	const lbNext = () => setLbIndex(i => (i + 1) % Math.max(1, (lbImages || []).length));
	const lbPrev = () => setLbIndex(i => (i - 1 + Math.max(1, (lbImages || []).length)) % Math.max(1, (lbImages || []).length));

	const setImageIndexForPost = (postId, idx) => {
		setImageIndexes(prev => ({ ...prev, [postId]: idx }));
	};
	const nextImage = (postId) => {
		setImageIndexes(prev => {
			const current = prev[postId] || 0;
			const p = posts.find(x => (x.Id || x.id) == postId);
			const len = (p && p.files && p.files.length) || 0;
			return { ...prev, [postId]: (current + 1) % Math.max(1, len) };
		});
	};
	const prevImage = (postId) => {
		setImageIndexes(prev => {
			const current = prev[postId] || 0;
			const p = posts.find(x => (x.Id || x.id) == postId);
			const len = (p && p.files && p.files.length) || 0;
			return { ...prev, [postId]: (current - 1 + Math.max(1, len)) % Math.max(1, len) };
		});
	};

	const [showCreateGroup, setShowCreateGroup] = useState(false);
	const [showGroupsDropdown, setShowGroupsDropdown] = useState(false);
	const [showAllGroups, setShowAllGroups] = useState(false);
	const [sidebarGroups, setSidebarGroups] = useState([]);
	const [sidebarGroupsLoading, setSidebarGroupsLoading] = useState(false);
	const [sidebarGroupsError, setSidebarGroupsError] = useState('');
	const [selectedGroup, setSelectedGroup] = useState(null);
	// Set of group IDs the user belongs to — used to filter group posts in the feed
	const myGroupIdsRef = useRef(new Set());
	// Map of groupId → group name — used to display group name on feed posts
	const myGroupNamesRef = useRef({});
	const navigate = useNavigate();

	// ── Minhas Conexões (sidebar) ──
	const [minhasConexoes, setMinhasConexoes] = useState([]);
	const [conexoesLoading, setConexoesLoading] = useState(false);

	// ── Cache de fotos de perfil ──
	const [userPhotos, setUserPhotos] = useState({});
	const userPhotosFetchingRef = useRef(new Set());

	const fetchUserPhoto = useCallback(async (uid) => {
		if (!uid || userPhotosFetchingRef.current.has(uid)) return;
		userPhotosFetchingRef.current.add(uid);
		try {
			const token = getAuthToken();
			const bases = [BASE_URL.replace(/\/api\/?$/, '')];
			for (const base of bases) {
				try {
					const res = await fetch(`${base}/api/DadosUsuario/foto-por-usuario/${uid}`, {
						headers: token ? { 'Authorization': `Bearer ${token}` } : {}
					});
					if (res.ok) {
						const blob = await res.blob();
						if (blob.size > 0) {
							const url = URL.createObjectURL(blob);
							setUserPhotos(prev => ({ ...prev, [uid]: url }));
							return;
						}
					}
					if (res.status === 404) {
						setUserPhotos(prev => ({ ...prev, [uid]: null }));
						return;
					}
				} catch (e) { continue; }
			}
			setUserPhotos(prev => ({ ...prev, [uid]: null }));
		} catch (e) {
			setUserPhotos(prev => ({ ...prev, [uid]: null }));
		} finally {
			userPhotosFetchingRef.current.delete(uid);
		}
	}, []);

	// ── Pesquisa global (clientes + funcionários) ──
	const [searchTerm, setSearchTerm] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchLoading, setSearchLoading] = useState(false);
	const searchTimerRef = useRef(null);
	const searchWrapperRef = useRef(null);

	// Fecha o dropdown ao clicar fora
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
				setSearchOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const doSearch = useCallback(async (term) => {
		if (!term || term.trim().length < 2) {
			setSearchResults([]);
			setSearchOpen(false);
			return;
		}
		setSearchLoading(true);
		try {
			const lowerTerm = term.toLowerCase();
			const results = [];

			// 1) Buscar Clientes
			try {
				const clientesData = await apiCall('Cliente', 'GET');
				const clientes = Array.isArray(clientesData) ? clientesData : [];
				clientes.forEach(c => {
					const nome = c.Nome || c.nome || '';
					if (nome.toLowerCase().includes(lowerTerm)) {
						results.push({
							tipo: 'cliente',
							id: c.Id || c.id || c.id_Clientes || c.clienteId,
							nome: nome,
							cpf: c.CPF_CNPJ || c.cpfCnpj || c.cpF_CNPJ || '',
							raw: c
						});
					}
				});
			} catch (e) { console.debug('Erro ao buscar clientes para pesquisa', e); }

			// 2) Buscar Funcionários (UsuarioSistema)
			try {
				const headers = { 'Content-Type': 'application/json' };
				const token = getAuthToken();
				if (token) headers['Authorization'] = `Bearer ${token}`;
				let usuarios = [];
				const urlsUsuarios = ['/api/UsuarioSistema', `${BASE_URL}/UsuarioSistema`, `${BASE_URL}/UsuarioSistema`];
				for (const url of urlsUsuarios) {
					try {
						const res = await fetch(url, { headers });
						if (res.ok) { usuarios = await res.json(); break; }
					} catch (e) { continue; }
				}

				// Para cada usuário, buscar DadosUsuario para pegar o nome completo
				for (const u of (Array.isArray(usuarios) ? usuarios : [])) {
					const login = u.login || u.Login || '';
					let nomeCompleto = login;
					try {
						const urlsDados = [
							`/api/DadosUsuario/por-usuario/${u.id || u.Id}`,
							`${BASE_URL}/DadosUsuario/por-usuario/${u.id || u.Id}`
						];
						for (const urlD of urlsDados) {
							try {
								const resD = await fetch(urlD, { headers });
								if (resD.ok) {
									const dados = await resD.json();
									nomeCompleto = dados?.nomeUsuario || dados?.NomeUsuario || dados?.Nome || login;
									break;
								}
							} catch (e) { continue; }
						}
					} catch (e) { /* mantém login como nome */ }

					if (nomeCompleto.toLowerCase().includes(lowerTerm)) {
						results.push({
							tipo: 'funcionario',
							id: u.id || u.Id,
							nome: nomeCompleto,
							login: login,
							raw: u,
							isConnected: false // will check below
						});
					}
				}

				// For each funcionario, check if connected using specific endpoint
				const meId = getUsuarioSistemaId();
				if (meId) {
					for (const func of results.filter(r => r.tipo === 'funcionario')) {
						try {
							const urlsCheck = [
								`/api/ConexaoUsuario/${meId}/${func.id}`,
								`${BASE_URL}/ConexaoUsuario/${meId}/${func.id}`
							];
							for (const urlCheck of urlsCheck) {
								try {
									const resCheck = await fetch(urlCheck, { headers });
									if (resCheck.ok) {
										func.isConnected = true;
										break;
									}
								} catch (e) { continue; }
							}
						} catch (e) { /* not connected */ }
					}
				}
			} catch (e) { console.debug('Erro ao buscar usuários para pesquisa', e); }

			setSearchResults(results);
			setSearchOpen(results.length > 0);
			// Buscar fotos de perfil dos funcionários encontrados
			results.filter(r => r.tipo === 'funcionario').forEach(r => {
				if (!(r.id in userPhotos)) fetchUserPhoto(r.id);
			});
		} catch (err) {
			console.error('Erro na pesquisa global', err);
		} finally {
			setSearchLoading(false);
		}
	}, []);

	const handleSearchChange = (e) => {
		const val = e.target.value;
		setSearchTerm(val);
		if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
		if (!val || val.trim().length < 2) {
			setSearchResults([]);
			setSearchOpen(false);
			return;
		}
		searchTimerRef.current = setTimeout(() => doSearch(val), 400);
	};

	const handleSearchResultClick = (item) => {
		setSearchOpen(false);
		setSearchTerm('');
		setSearchResults([]);
		if (item.tipo === 'cliente') {
			navigate('/sidebar/Cliente', { state: { editClienteId: item.id } });
		} else if (item.tipo === 'funcionario') {
			navigate('/sidebar/Funcionario', { state: { funcionario: item.raw } });
		}
	};

	// Circuit breaker state for API calls to avoid retry loops when backend is down
	const circuit = useRef({ failCount: 0, lockedUntil: 0, maxFailures: 3, cooldownBase: 5000 });
	// simple in-memory cache for certain GET calls (prevents duplicate GET /Grupo flooding)
	const apiCacheRef = useRef({}); // { key: { expires: number, promise: Promise } }

	const markApiFailure = () => {
		const c = circuit.current;
		c.failCount = Math.min(30, c.failCount + 1);
		if (c.failCount >= c.maxFailures) {
			const extra = c.failCount - c.maxFailures;
			c.lockedUntil = Date.now() + c.cooldownBase * Math.pow(2, extra);
		}
	};

	const markApiSuccess = () => {
		const c = circuit.current;
		c.failCount = 0;
		c.lockedUntil = 0;
	};

	// Periodic health-check to try re-opening the circuit when backend recovers
	useEffect(() => {
		const id = setInterval(async () => {
			const c = circuit.current;
			if (Date.now() < c.lockedUntil) {
				try {
					const hcHeaders = {};
					const hcToken = getAuthToken();
					if (hcToken) hcHeaders['Authorization'] = `Bearer ${hcToken}`;
					const res = await fetch('/api/Postagem', { method: 'GET', headers: hcHeaders });
					if (res && res.ok) {
						markApiSuccess();
						console.info('API recovered — circuit closed');
					}
				} catch (e) { /* still down */ }
			}
		}, 30000);
		return () => clearInterval(id);
	}, []);

	// No header detection here; App.js handles global header fix and sets --global-header-height

	// Posting UI moved to FormularioPostagem component

	// API helpers (local fallbacks similar to ProjetoVisualDropdown)
	const apiCall = async (endpoint, method = 'GET', data = null) => {

		// circuit check: avoid trying when circuit open
		if (Date.now() < circuit.current.lockedUntil) {
			throw new Error('API offline (circuit open)');
		}

		// simple caching for GET /Grupo to reduce duplicate requests and speed things up
		if ((method || 'GET').toUpperCase() === 'GET') {
			const key = String(endpoint || '').toLowerCase();
			if (key === 'grupo' || key === '/grupo') {
				const cache = apiCacheRef.current[key];
				if (cache && cache.expires > Date.now()) {
					// return cached promise (await it to keep same behavior)
					return await cache.promise;
				}

				// create a promise for the actual fetch and store it immediately so concurrent callers share it
				const p = (async () => {
					let lastErr = null;
					const urls = [
						`/api/${endpoint}`,
						`${BASE_URL}/${endpoint}`,
						`${BASE_URL}/${endpoint}`
					];
					for (const url of urls) {
						try {
							console.debug('apiCall trying', method, url);
							const headers = { 'Content-Type': 'application/json' };
							const token = getAuthToken();
							if (token) headers['Authorization'] = `Bearer ${token}`;
							const res = await fetch(url, { method: 'GET', headers });
							if (res.ok || (res.status >= 200 && res.status < 300)) {
								markApiSuccess();
								const text = await res.text();
								try { return JSON.parse(text); } catch { return text; }
							}
							markApiFailure();
							lastErr = new Error(`${res.status} ${res.statusText}`);
						} catch (e) {
							console.debug('apiCall failed for', url, e && e.message ? e.message : e);
							markApiFailure();
							lastErr = e;
							continue;
						}
					}
					throw lastErr || new Error(`Nenhuma URL da API funcionou para GET ${endpoint}`);
				})();

				apiCacheRef.current[key] = { promise: p, expires: Date.now() + 30000 }; // cache 30s
				return await p;
			}
		}

		// Prefer same-origin relative API first (avoids mixed-content issues), then try local http/https
		const urls = [
			`/api/${endpoint}`,
			`${BASE_URL}/${endpoint}`,
			`${BASE_URL}/${endpoint}`
		];

		let lastErr = null;
		for (const url of urls) {
			try {
				console.debug('apiCall trying', method, url);
				const headers = { 'Content-Type': 'application/json' };
				const token = getAuthToken();
				if (token) headers['Authorization'] = `Bearer ${token}`;
				const config = { method, headers };
				if (data && (method === 'POST' || method === 'PUT')) config.body = JSON.stringify(data);
				const res = await fetch(url, config);
				if (res.ok || (res.status >= 200 && res.status < 300)) {
					markApiSuccess();
					const ct = res.headers.get('Content-Type') || '';
					if (ct.includes('application/json')) {
						try { return await res.json(); } catch { return { success: true, status: res.status }; }
					}
					return await res.text();
				}
				// non-2xx response -> mark failure and continue
				markApiFailure();
				lastErr = new Error(`${res.status} ${res.statusText}`);
			} catch (e) {
				console.debug('apiCall failed for', url, e && e.message ? e.message : e);
				markApiFailure();
				lastErr = e;
				continue;
			}
		}
		throw lastErr || new Error(`Nenhuma URL da API funcionou para ${method} ${endpoint}`);
	};

	// Upload helper that accepts FormData and tries relative `/api` then local backends
	const apiUploadMultipart = async (endpoint, formData) => {
		// circuit check: avoid trying when circuit open
		if (Date.now() < circuit.current.lockedUntil) {
			throw new Error('API offline (circuit open)');
		}

		const bases = ['', BASE_URL.replace(/\/api\/?$/, '')];
		let lastErr = null;
		for (const base of bases) {
			const url = `${base}/api/${endpoint}`.replace('//api/api', '/api');
			try {
				console.debug('apiUploadMultipart trying', url);
				const headers = {};
				const token = getAuthToken();
				if (token) headers['Authorization'] = `Bearer ${token}`;
				const res = await fetch(url, { method: 'POST', body: formData, headers });
				if (!res.ok) {
					markApiFailure();
					throw new Error(`${res.status} ${res.statusText}`);
				}
				markApiSuccess();
				const text = await res.text();
				try { return JSON.parse(text); } catch { return text; }
			} catch (err) {
				console.debug('apiUploadMultipart failed for', url, err && err.message ? err.message : err);
				lastErr = err;
				markApiFailure();
				continue;
			}
		}
		throw lastErr || new Error('Falha ao enviar arquivos');
	};

	// Edit post helpers
	const startEditPost = (p) => {
		const pid = p.Id || p.id;
		setEditingPostId(pid);
		setEditingText(p.Descricao || p.descricao || p.description || '');
		// Populate edit-mode files from post
		const allFiles = p.files || [];
		const imgs = allFiles.filter(f => f && f.type && (f.type.startsWith('image/') || f.type.startsWith('video/')));
		const others = allFiles.filter(f => f && !(f.type && (f.type.startsWith('image/') || f.type.startsWith('video/'))));
		setEditImages(imgs.map(f => ({ ...f, isNew: false })));
		setEditFiles(others.map(f => ({ ...f, isNew: false })));
		setEditNewImages([]);
		setEditNewFiles([]);
		setEditRemovedFileIds([]);
		setShowVerTodos(false);
		setVerTodosSelectedImages(new Set());
		setVerTodosSelectedFiles(new Set());
		setConfirmBulkDelete(false);
	};
	const cancelEdit = () => {
		setEditingPostId(null);
		setEditingText('');
		setEditImages([]);
		setEditFiles([]);
		setEditNewImages([]);
		setEditNewFiles([]);
		setEditRemovedFileIds([]);
		setShowVerTodos(false);
		setVerTodosSelectedImages(new Set());
		setVerTodosSelectedFiles(new Set());
		setConfirmBulkDelete(false);
	};
	const saveEditPost = async (postId) => {
		setEditingSaving(true);
		try {
			// 1) Update text
			await apiCall(`Postagem/${postId}`, 'PUT', { Descricao: editingText });
			// 2) Delete removed files from server
			for (const fId of editRemovedFileIds) {
				try { await apiCall(`PostagemArquivo/${fId}`, 'DELETE'); } catch (e) { console.warn('Erro ao deletar arquivo', fId, e); }
			}
			// 3) Upload new images
			for (const file of editNewImages) {
				const fd = new FormData();
				fd.append('File', file);
				fd.append('PostagemId', String(postId));
				try { await apiUploadMultipart('PostagemArquivo', fd); } catch (e) { console.warn('Erro ao enviar imagem', e); }
			}
			// 4) Upload new non-image files
			for (const file of editNewFiles) {
				const fd = new FormData();
				fd.append('File', file);
				fd.append('PostagemId', String(postId));
				try { await apiUploadMultipart('PostagemArquivo', fd); } catch (e) { console.warn('Erro ao enviar arquivo', e); }
			}
			// 5) Re-fetch post files so UI updates
			const currentPost = posts.find(pp => (pp.Id || pp.id) == postId);
			if (currentPost) {
				const enriched = await fetchFilesForPost({ ...currentPost, Descricao: editingText, descricao: editingText, description: editingText });
				setPosts(prev => prev.map(pp => (pp.Id || pp.id) == postId ? enriched : pp));
			} else {
				setPosts(prev => prev.map(pp => {
					const pid = pp.Id || pp.id;
					if (pid == postId) return { ...pp, Descricao: editingText, descricao: editingText, description: editingText };
					return pp;
				}));
			}
			cancelEdit();
		} catch (err) {
			console.error('Erro ao editar postagem:', err);
			alert('Erro ao salvar edição: ' + (err.message || err));
		} finally {
			setEditingSaving(false);
		}
	};
	// Remove a single image from edit (by index); if it has an id, mark for server deletion
	const editRemoveImage = (index) => {
		const img = editImages[index];
		if (img && img.id && !img.isNew) {
			setEditRemovedFileIds(prev => [...prev, img.id]);
		}
		if (img && img.isNew) {
			// find which new image to remove by matching the temp url
			setEditNewImages(prev => prev.filter((_, i) => {
				// match by url since we assigned blob url
				return editImages.filter(ei => ei.isNew)[i]?._tempUrl !== img._tempUrl;
			}));
		}
		setEditImages(prev => prev.filter((_, i) => i !== index));
	};
	// Remove a single non-image file from edit
	const editRemoveFile = (index) => {
		const f = editFiles[index];
		if (f && f.id && !f.isNew) {
			setEditRemovedFileIds(prev => [...prev, f.id]);
		}
		if (f && f.isNew) {
			setEditNewFiles(prev => prev.filter((_, i) => {
				return editFiles.filter(ef => ef.isNew)[i]?._tempUrl !== f._tempUrl;
			}));
		}
		setEditFiles(prev => prev.filter((_, i) => i !== index));
	};
	// Add new images via file input
	const editAddImages = (fileList) => {
		const newFiles = Array.from(fileList);
		const newEntries = newFiles.map(file => {
			const url = URL.createObjectURL(file);
			return { id: null, url, type: file.type, name: file.name, isNew: true, _tempUrl: url, file };
		});
		setEditImages(prev => [...prev, ...newEntries]);
		setEditNewImages(prev => [...prev, ...newFiles]);
	};
	// Add new non-image files via file input
	const editAddFiles = (fileList) => {
		const newFiles = Array.from(fileList);
		const newEntries = newFiles.map(file => {
			const url = URL.createObjectURL(file);
			return { id: null, url, type: file.type, name: file.name, NomeArquivo: file.name, isNew: true, _tempUrl: url, file };
		});
		setEditFiles(prev => [...prev, ...newEntries]);
		setEditNewFiles(prev => [...prev, ...newFiles]);
	};
	// Bulk delete from "Ver todos" popup
	const bulkDeleteFiles = () => {
		// Remove selected images
		const imgIdsToRemove = [...verTodosSelectedImages];
		const fileIdsToRemove = [...verTodosSelectedFiles];
		// Mark existing files for server deletion
		editImages.forEach((img, i) => {
			if (imgIdsToRemove.includes(i) && img.id && !img.isNew) {
				setEditRemovedFileIds(prev => [...prev, img.id]);
			}
		});
		editFiles.forEach((f, i) => {
			if (fileIdsToRemove.includes(i) && f.id && !f.isNew) {
				setEditRemovedFileIds(prev => [...prev, f.id]);
			}
		});
		// Remove new file references
		const newImgsToRemove = imgIdsToRemove.filter(i => editImages[i]?.isNew);
		if (newImgsToRemove.length > 0) {
			const tempUrls = new Set(newImgsToRemove.map(i => editImages[i]?._tempUrl));
			setEditNewImages(prev => prev.filter((_, idx) => {
				const entry = editImages.filter(ei => ei.isNew)[idx];
				return entry && !tempUrls.has(entry._tempUrl);
			}));
		}
		const newFilesToRemove = fileIdsToRemove.filter(i => editFiles[i]?.isNew);
		if (newFilesToRemove.length > 0) {
			const tempUrls = new Set(newFilesToRemove.map(i => editFiles[i]?._tempUrl));
			setEditNewFiles(prev => prev.filter((_, idx) => {
				const entry = editFiles.filter(ef => ef.isNew)[idx];
				return entry && !tempUrls.has(entry._tempUrl);
			}));
		}
		setEditImages(prev => prev.filter((_, i) => !imgIdsToRemove.includes(i)));
		setEditFiles(prev => prev.filter((_, i) => !fileIdsToRemove.includes(i)));
		setVerTodosSelectedImages(new Set());
		setVerTodosSelectedFiles(new Set());
		setConfirmBulkDelete(false);
		setShowVerTodos(false);
	};
	const deletePost = async (postId) => {
		try {
			await apiCall(`Postagem/${postId}`, 'DELETE');
			setPosts(prev => prev.filter(p => (p.Id || p.id) != postId));
		} catch (err) {
			console.error('Erro ao excluir postagem:', err);
			alert('Erro ao excluir postagem: ' + (err.message || err));
		} finally {
			setConfirmDeletePostId(null);
		}
	};

	// Fetch a file from PostagemArquivo/{id} with auth headers -> blob URL
	const fetchFileBlobUrl = async (fileId) => {
		const token = getAuthToken();
		const bases = ['', BASE_URL.replace(/\/api\/?$/, '')];
		for (const base of bases) {
			try {
				const url = `${base}/api/PostagemArquivo/${fileId}`.replace('//api/api', '/api');
				const res = await fetch(url, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} });
				if (res.ok) {
					const blob = await res.blob();
					const objUrl = URL.createObjectURL(blob);
					try { postObjectUrlRefs.current[fileId] = postObjectUrlRefs.current[fileId] || []; postObjectUrlRefs.current[fileId].push(objUrl); } catch (e) {}
					return objUrl;
				}
			} catch (e) { continue; }
		}
		return null;
	};

	// onPost is handled inside FormularioPostagem

	// onCancel handled in FormularioPostagem

	// Cache de nomes de usuários por UsuarioSistemaId
	const [userNamesById, setUserNamesById] = useState({});
	const userNamesFetchingRef = useRef(new Set());

	// Busca o nome do usuário via DadosUsuario/por-usuario/{usuarioSistemaId}
	const fetchUserName = async (usuarioSistemaId) => {
		if (!usuarioSistemaId || userNamesById[usuarioSistemaId] || userNamesFetchingRef.current.has(usuarioSistemaId)) return;
		userNamesFetchingRef.current.add(usuarioSistemaId);
		try {
			const data = await apiCall(`DadosUsuario/por-usuario/${usuarioSistemaId}`, 'GET');
			const nome = data?.NomeUsuario || data?.nomeUsuario || data?.Nome || data?.nome || null;
			if (nome) {
				setUserNamesById(prev => ({ ...prev, [usuarioSistemaId]: nome }));
			}
		} catch (err) {
			console.debug('Erro ao buscar nome do usuário', usuarioSistemaId, err);
		} finally {
			userNamesFetchingRef.current.delete(usuarioSistemaId);
		}
	};

	// Helper para obter nome do usuário ou fallback
	const getUserName = (usuarioSistemaId) => {
		if (!usuarioSistemaId) return null;
		return userNamesById[usuarioSistemaId] || null;
	};

	// Emoji list and behaviors
	const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = Mais recente, 'asc' = Mais antigo
	const [dateFilter, setDateFilter] = useState('');
	const dateInputRef = useRef(null);
	const [likedPosts, setLikedPosts] = useState(new Set());const [refreshingLatest, setRefreshingLatest] = useState(false);	const [likesCountByPost, setLikesCountByPost] = useState({});
	const [myLikeIdByPost, setMyLikeIdByPost] = useState({});
	const [commentsOpenPosts, setCommentsOpenPosts] = useState(new Set());
	const [commentInputs, setCommentInputs] = useState({});
	const [commentsByPost, setCommentsByPost] = useState({});
	const [commentsCountByPost, setCommentsCountByPost] = useState({});

	const toggleLike = async (p) => {
		const pid = p.Id || p.id || p.PostagemId || p.postagemId || p.PostagemID || p.postagemID || null;
		if (!pid) return;

		const isLiked = likedPosts.has(pid);

		// Optimistic update
		setLikedPosts(prev => { const next = new Set(prev); if (isLiked) next.delete(pid); else next.add(pid); return next; });
		setLikesCountByPost(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) + (isLiked ? -1 : 1)) }));

		// If currently not liked, create a PostagemCurtida with UsuarioId = null
		if (!isLiked) {
			try {
				const created = await apiCall('PostagemCurtida', 'POST', { PostagemId: pid });
				const createdId = created?.Id || created?.id || created?.postagemCurtidaId || null;
				if (createdId) setMyLikeIdByPost(prev => ({ ...prev, [pid]: createdId }));
				// refresh counts from server to be consistent
				try { const likeRes = await apiCall(`PostagemCurtida/postagem/${pid}`, 'GET'); if (likeRes && typeof likeRes.count === 'number') setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes.count })); else if (Array.isArray(likeRes)) setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes.length })); } catch (e){}
			} catch (err) {
				// revert optimistic
				setLikedPosts(prev => { const next = new Set(prev); next.delete(pid); return next; });
				setLikesCountByPost(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 1) - 1) }));
				console.error('Erro ao curtir publicação', err);
				alert('Erro ao curtir publicação: ' + (err.message || err));
			}
		} else {
			// currently liked -> try to remove like via DELETE if we have an id
			const likeId = myLikeIdByPost[pid];
			if (likeId) {
				try {
					await apiCall(`PostagemCurtida/${likeId}`, 'DELETE');
					setMyLikeIdByPost(prev => { const copy = { ...prev }; delete copy[pid]; return copy; });
					// refresh counts
					try { const likeRes = await apiCall(`PostagemCurtida/postagem/${pid}`, 'GET'); if (likeRes && typeof likeRes.count === 'number') setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes.count })); else if (Array.isArray(likeRes)) setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes.length })); } catch (e){}
				} catch (err) {
					// revert optimistic
					setLikedPosts(prev => { const next = new Set(prev); next.add(pid); return next; });
					setLikesCountByPost(prev => ({ ...prev, [pid]: (prev[pid] || 0) + 1 }));
					console.error('Erro ao remover curtida', err);
					alert('Erro ao remover curtida: ' + (err.message || err));
				}
			} else {
				// no server-side id known -> attempt to find the like record on server and delete it
				try {
					const likeResAll = await apiCall(`PostagemCurtida/postagem/${pid}`, 'GET');
					const items = Array.isArray(likeResAll) ? likeResAll : (likeResAll?.items || []);
					let found = null;
					if (items && items.length) {
						found = items.find(i => i && (i.Id || i.id) && (i.UsuarioId === null || i.usuarioId === null || i.UsuarioId === undefined || i.usuarioId === undefined));
						if (!found) found = items[0];
					}
					const fid = found ? (found.Id || found.id) : null;
					if (fid) {
						await apiCall(`PostagemCurtida/${fid}`, 'DELETE');
						setMyLikeIdByPost(prev => { const copy = { ...prev }; delete copy[pid]; return copy; });
						// refresh counts
						try { const likeRes2 = await apiCall(`PostagemCurtida/postagem/${pid}`, 'GET'); if (likeRes2 && typeof likeRes2.count === 'number') setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes2.count })); else if (Array.isArray(likeRes2)) setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes2.length })); } catch (e){}
					} else {
						// couldn't find server id: revert optimistic change and notify
						setLikedPosts(prev => { const next = new Set(prev); next.add(pid); return next; });
						setLikesCountByPost(prev => ({ ...prev, [pid]: (prev[pid] || 0) + 1 }));
						alert('Não foi possível localizar a curtida no servidor para deletar.');
					}
				} catch (err) {
					// revert optimistic
					setLikedPosts(prev => { const next = new Set(prev); next.add(pid); return next; });
					setLikesCountByPost(prev => ({ ...prev, [pid]: (prev[pid] || 0) + 1 }));
					console.error('Erro ao tentar remover curtida (sem id conhecido)', err);
					alert('Erro ao remover curtida: ' + (err.message || err));
				}
			}
		}
	};

	const [commentsLoadingPosts, setCommentsLoadingPosts] = useState(new Set());
	const toggleComments = async (p) => {
		const id = p.Id || p.id || null;
		if (!id) return;
		// Toggle open state immediately (optimistic) so UI expands before fetch
		let nowOpen = false;
		setCommentsOpenPosts(prev => {
			const next = new Set(prev);
			if (next.has(id)) { next.delete(id); nowOpen = false; } else { next.add(id); nowOpen = true; }
			return next;
		});

		// If we just opened, fetch comments in background
		if (nowOpen) {
			setCommentsLoadingPosts(prev => new Set(prev).add(id));
			try {
				const data = await apiCall(`PostagemComentario/postagem/${id}`, 'GET');
				const arr = Array.isArray(data) ? data : [];
				setCommentsByPost(prev => ({ ...prev, [id]: arr }));
				setCommentsCountByPost(prev => ({ ...prev, [id]: arr.length }));
			} catch (err) {
				console.error('Erro ao carregar comentários', err);
				setCommentsByPost(prev => ({ ...prev, [id]: [] }));
				setCommentsCountByPost(prev => ({ ...prev, [id]: 0 }));
			} finally {
				setCommentsLoadingPosts(prev => { const s = new Set(prev); s.delete(id); return s; });
			}
		}
	};

	const setCommentInputForPost = (postId, value) => setCommentInputs(prev => ({ ...prev, [postId]: value }));
	const submitCommentForPost = async (postId) => {
		const txt = (commentInputs[postId] || '').trim();
		if (!txt) return;
		// optimistic UI: clear input and append a temporary comment
		setCommentInputs(prev => ({ ...prev, [postId]: '' }));
		const temp = { id: 'temp-' + Date.now(), conteudo: txt, DataHora: new Date().toISOString(), UsuarioId: null };
		setCommentsByPost(prev => ({ ...prev, [postId]: [ ...(prev[postId] || []), temp ] }));
		setCommentsCountByPost(prev => ({ ...prev, [postId]: (prev[postId] || 0) + 1 }));
		try {
			const created = await apiCall('PostagemComentario', 'POST', { PostagemId: postId, Conteudo: txt });
			// server returns created dto; merge it replacing temp
			setCommentsByPost(prev => ({ ...prev, [postId]: [ ...(prev[postId] || []).map(c => (c.id === temp.id ? created : c)) ] }));
		} catch (err) {
			console.error('Erro ao enviar comentário', err);
			alert('Erro ao enviar comentário: ' + (err.message || err));
			// remove temp comment
			setCommentsByPost(prev => ({ ...prev, [postId]: (prev[postId] || []).filter(c => c.id !== temp.id) }));
			setCommentsCountByPost(prev => ({ ...prev, [postId]: Math.max(0, (prev[postId] || 1) - 1) }));
		}
	};

	// image/video/file upload handlers moved to FormularioPostagem


	// load groups helper so we can refresh after creating a new group
	// Agora filtra apenas grupos que o usuário criou OU participa
	const loadSidebarGroups = async () => {
		setSidebarGroupsLoading(true);
		try {
			const meuId = getUsuarioSistemaId();
			if (!meuId) {
				setSidebarGroups([]);
				return;
			}
			
			// Buscar todos os grupos
			const data = await apiCall('Grupo', 'GET');
			const todosGrupos = Array.isArray(data) ? data : (data?.Grupos || data?.grupos || []);
			
			// Buscar grupos que o usuário participa (via UsuariosGrupo/usuario/{id})
			let gruposParticipo = [];
			try {
				const usuarioGrupos = await apiCall(`UsuariosGrupo/usuario/${meuId}`, 'GET');
				const arrUG = Array.isArray(usuarioGrupos) ? usuarioGrupos : [];
				gruposParticipo = arrUG.map(ug => ug.GrupoId || ug.grupoId);
			} catch (e) {
				console.warn('Não foi possível buscar UsuariosGrupo:', e);
			}
			
			// Filtrar: grupos que eu criei OU grupos que eu participo
			const meusGrupos = todosGrupos.filter(g => {
				const grupoId = g.Id || g.id;
				const criadorId = g.UsuarioSistemaId || g.usuarioSistemaId || g.CriadoPor || g.criadoPor;
				// Grupo que eu criei
				if (criadorId === meuId) return true;
				// Grupo que eu participo
				if (gruposParticipo.includes(grupoId)) return true;
				return false;
			});
			
			setSidebarGroups(meusGrupos);
			// Populate refs for post-feed filtering and group name lookup
			const idSet = new Set();
			const nameMap = {};
			meusGrupos.forEach(g => {
				const gid = g.Id || g.id;
				if (gid) {
					idSet.add(gid);
					idSet.add(String(gid));
					nameMap[gid] = g.NomeGrupo || g.nomeGrupo || g.Nome || g.nome || '';
				}
			});
			myGroupIdsRef.current = idSet;
			myGroupNamesRef.current = nameMap;
			setSidebarGroupsError('');
		} catch (err) {
			console.error('Erro ao carregar grupos (sidebar)', err);
			setSidebarGroupsError('Erro ao carregar grupos');
			setSidebarGroups([]);
		} finally {
			setSidebarGroupsLoading(false);
		}
	};

	// Carregar minhas conexões para o sidebar
	const loadMinhasConexoes = async () => {
		setConexoesLoading(true);
		try {
			const meuId = getUsuarioSistemaId();
			if (!meuId) {
				setMinhasConexoes([]);
				return;
			}
			
			// Buscar conexões onde eu sou a origem
			const conexoes = await apiCall('ConexaoUsuario', 'GET');
			const arrConexoes = Array.isArray(conexoes) ? conexoes : [];
			const minhasConexoesIds = arrConexoes
				.filter(c => (c.UsuarioIdOrigem || c.usuarioIdOrigem) === meuId)
				.map(c => c.UsuarioIdDestino || c.usuarioIdDestino);
			
			if (minhasConexoesIds.length === 0) {
				setMinhasConexoes([]);
				return;
			}
			
			// Buscar dados dos usuários conectados via DadosUsuario
			const usuariosConectados = [];
			for (const usuarioSistemaId of minhasConexoesIds) {
				try {
					// Buscar dados do usuário (login)
					const usuarioSistema = await apiCall(`UsuarioSistema/${usuarioSistemaId}`, 'GET');
					const login = usuarioSistema?.Login || usuarioSistema?.login || '';
					
					// Buscar nome via DadosUsuario
					let nomeCompleto = login;
					try {
						const dadosUsuario = await apiCall(`DadosUsuario/por-usuario/${usuarioSistemaId}`, 'GET');
						nomeCompleto = dadosUsuario?.nomeUsuario || dadosUsuario?.NomeUsuario || dadosUsuario?.Nome || dadosUsuario?.nome || login;
					} catch (e) { /* mantém login como nome */ }
					
					usuariosConectados.push({
						id: usuarioSistemaId,
						nome: nomeCompleto,
						login: login
					});
				} catch (e) {
					console.error('Erro ao buscar dados do usuário:', e);
				}
			}
			
			setMinhasConexoes(usuariosConectados);
			// Buscar fotos de perfil das conexões
			usuariosConectados.forEach(u => {
				if (!(u.id in userPhotos)) fetchUserPhoto(u.id);
			});
		} catch (err) {
			console.error('Erro ao carregar conexões:', err);
			setMinhasConexoes([]);
		} finally {
			setConexoesLoading(false);
		}
	};

	// helper to fetch files for a post (images/attachments)
	const fetchFilesForPost = async (p) => {
		try {
			const pid = p.Id || p.id || p.PostagemId || p.postagemId;
			if (!pid) return p;
			const fa = await apiCall(`PostagemArquivo/postagem/${pid}`, 'GET');
			const arrFiles = Array.isArray(fa) ? fa : [];
			let files = (await Promise.all((arrFiles || []).map(async f => {
				const type = f.TipoArquivo || f.tipoArquivo || f.tipo || 'application/octet-stream';
				let url = null;
				// server may return base64 string
				if (typeof f.Arquivo === 'string' && f.Arquivo.trim().length > 0) {
					url = `data:${type};base64,` + f.Arquivo;
				// or an array of bytes
				} else if (Array.isArray(f.Arquivo) && f.Arquivo.length) {
					const b = Uint8Array.from(f.Arquivo);
					url = URL.createObjectURL(new Blob([b], { type }));
					try { postObjectUrlRefs.current[pid] = postObjectUrlRefs.current[pid] || []; postObjectUrlRefs.current[pid].push(url); } catch (e) {}
				}
				// fallback: fetch with auth headers to avoid tenant middleware 400
				if (!url && (f.Id || f.id)) {
					const fid = f.Id || f.id;
					try { url = await fetchFileBlobUrl(fid); } catch (e) { url = null; }
				}
				const fileName = f.NomeArquivo || f.nomeArquivo || f.Nome || f.nome || f.fileName || f.file || f.name || '';
				return { id: f.Id || f.id || null, type, url, name: fileName, NomeArquivo: f.NomeArquivo || f.nomeArquivo || f.Nome || f.nome };
			}))).filter(Boolean);



			// detect portrait/square images and annotate files with isPortrait/isSquare
			const detectImageOrientation = (url) => new Promise(resolve => {
				try {
					const img = new Image();
					img.onload = () => {
						const w = img.naturalWidth;
						const h = img.naturalHeight;
						// Square: height and width equal or difference < 5%
						const ratio = Math.abs(h - w) / Math.max(h, w, 1);
						const isSquare = ratio < 0.05;
						const isPortrait = !isSquare && h > w;
						resolve({ isPortrait, isSquare });
					};
					img.onerror = () => { resolve({ isPortrait: false, isSquare: false }); };
					img.src = url;
				} catch (e) { resolve({ isPortrait: false, isSquare: false }); }
			});

			files = await Promise.all(files.map(async (f) => {
				if (f.type && f.type.startsWith('image/') && f.url) {
					try {
						const { isPortrait, isSquare } = await detectImageOrientation(f.url);
						f.isPortrait = isPortrait;
						f.isSquare = isSquare;
					} catch (e) { f.isPortrait = false; f.isSquare = false; }
				} else {
					f.isPortrait = false;
					f.isSquare = false;
				}
				return f;
			}));
			// also prefetch comment counts for this post so counts are available before expanding
			try {
				const pid = p.Id || p.id || p.PostagemId || p.postagemId;
				if (pid) {
					const comRes = await apiCall(`PostagemComentario/postagem/${pid}`, 'GET');
					if (Array.isArray(comRes)) setCommentsCountByPost(prev => ({ ...prev, [pid]: comRes.length }));
					else if (comRes && typeof comRes.count === 'number') setCommentsCountByPost(prev => ({ ...prev, [pid]: comRes.count }));
				}
			} catch (err) { setCommentsCountByPost(prev => ({ ...prev, [p.Id || p.id]: 0 })); }
			return { ...p, files };
		} catch (e) { return { ...p, files: [] }; }
	};

	// helper: fetch likes/comments counts for an array of posts (non-blocking)
	const fetchCountsForPosts = async (postsArray) => {
		if (!postsArray || postsArray.length === 0) return;
		await Promise.all(postsArray.map(async (mp) => {
			const pid = mp.Id || mp.id || mp.PostagemId || mp.postagemId;
			if (!pid) return;
			try {
				const likeRes = await apiCall(`PostagemCurtida/postagem/${pid}`, 'GET');
				if (likeRes && typeof likeRes.count === 'number') setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes.count }));
				else if (Array.isArray(likeRes)) setLikesCountByPost(prev => ({ ...prev, [pid]: likeRes.length }));
				// Try to detect if the current user (or anonymous local user) has liked this post.
				// Server may return an object with `items` or an array of items.
				let items = null;
				if (likeRes && Array.isArray(likeRes)) items = likeRes;
				else if (likeRes && likeRes.items && Array.isArray(likeRes.items)) items = likeRes.items;
				if (items && items.length) {
					// Prefer a like record that seems to belong to this client: UsuarioId === null or undefined
					const myLike = items.find(i => i.UsuarioId === null || i.usuarioId === null || i.UsuarioId === undefined || i.usuarioId === undefined);
					if (myLike && (myLike.Id || myLike.id)) {
						setMyLikeIdByPost(prev => ({ ...prev, [pid]: myLike.Id || myLike.id }));
						setLikedPosts(prev => { const s = new Set(prev); s.add(pid); return s; });
					}
				}
			} catch (e) { setLikesCountByPost(prev => ({ ...prev, [pid]: 0 })); }
			try {
				const comRes = await apiCall(`PostagemComentario/postagem/${pid}`, 'GET');
				if (Array.isArray(comRes)) setCommentsCountByPost(prev => ({ ...prev, [pid]: comRes.length }));
				else if (comRes && typeof comRes.count === 'number') setCommentsCountByPost(prev => ({ ...prev, [pid]: comRes.count }));
			} catch (e) { setCommentsCountByPost(prev => ({ ...prev, [pid]: 0 })); }
		}));
	};

	// Merge incoming posts into current posts avoiding duplicates by post id.
	// If `prepend` is true, incoming posts will appear before existing posts.
	const mergeUniquePosts = (prevPosts, incomingPosts, prepend = false) => {
		const map = new Map();
		const push = (p) => {
			const id = p?.Id || p?.id || p?.PostagemId || p?.postagemId || p?.createdAt || JSON.stringify(p);
			if (!map.has(id)) map.set(id, p);
		};
		if (prepend) {
			(incomingPosts || []).forEach(push);
			(prevPosts || []).forEach(push);
		} else {
			(prevPosts || []).forEach(push);
			(incomingPosts || []).forEach(push);
		}
		return Array.from(map.values());
	};

	// load public posts helper with pagination (load 2 at a time). Accepts reset flag to restart pagination.
	const PAGE_SIZE = 2;
	const [skipCount, setSkipCount] = useState(0);
	const [hasMorePosts, setHasMorePosts] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);

	// refs to keep synchronous values for pagination/loading to avoid race conditions
	const skipRef = useRef(skipCount);
	const hasMoreRef = useRef(hasMorePosts);
	const loadingMoreRef = useRef(loadingMore);
	const bottomRef = useRef(null);

	useEffect(() => { skipRef.current = skipCount; }, [skipCount]);
	useEffect(() => { hasMoreRef.current = hasMorePosts; }, [hasMorePosts]);
	useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);

	const loadPublicPosts = async ({ reset = false } = {}) => {
		if (reset) {
			setSkipCount(0);
			setHasMorePosts(true);
			setPosts([]);
			skipRef.current = 0;
			hasMoreRef.current = true;
		}

		// if already loading more, skip
		if (loadingMoreRef.current) return;

		setPostsLoading(prev => reset ? true : prev);
		setLoadingMore(true);
		loadingMoreRef.current = true;
		setPostsError('');
		try {
			const baseSkip = reset ? 0 : (skipRef.current || 0);
			const endpointBase = (sortOrder === 'asc') ? `Postagem/antigo?skip=${baseSkip}&take=${PAGE_SIZE}` : `Postagem?skip=${baseSkip}&take=${PAGE_SIZE}`;
			let endpoint = endpointBase;
			if (dateFilter) {
				endpoint += `&date=${dateFilter}`; // backend supports optional date filter YYYY-MM-DD
			}

			// initial fetch for this page
			console.debug('loadPublicPosts: fetching', endpoint, 'reset?', reset, 'skipCount', skipCount);
			const initialData = await apiCall(endpoint, 'GET');
			let cumulativeArrCount = 0;
			let addedAny = false;
			let attempts = 0;
			let localBaseSkip = baseSkip;
			const maxAttempts = 10; // safety to avoid infinite loops if many results are filtered out
			let total = initialData && (typeof initialData.Total === 'number') ? initialData.Total : null;
			let arrLocal = (initialData && initialData.Items) ? initialData.Items : (Array.isArray(initialData) ? initialData : (initialData?.items || []));
			// track existing IDs to avoid duplicates and gather actually added posts for fetching counts
			const existingIds = new Set((posts || []).map(x => x.Id || x.id || x.PostagemId || x.postagemId));
			const addedPosts = [];
			while (true) {
				attempts++;
				const arr = arrLocal || [];
				cumulativeArrCount += arr.length;

				// Keep public posts OR group posts from groups I belong to
				const publicPosts = arr.filter(p => {
					if (!p) return false;
					const gid = p.GrupoId || p.grupoId;
					if (gid) {
						// Group post: only show if I'm a member of that group
						return myGroupIdsRef.current.has(gid) || myGroupIdsRef.current.has(String(gid));
					}
					// Public post: exclude if it has Grupo object/array (extra safety)
					if (p.Grupo) return false;
					if (p.Grupos && Array.isArray(p.Grupos) && p.Grupos.length) return false;
					return true;
				}).map(p => ({ ...p, createdAt: extractPostDate(p) }));

				// enrich with files for the newly loaded posts
				const enriched = await Promise.all(publicPosts.map(async (pp) => await fetchFilesForPost(pp)));

				// determine items to add (avoid duplicates based on snapshot existingIds)
				const add = enriched.filter(p => { const id = p.Id || p.id || p.PostagemId || p.postagemId; if (!id) return false; if (existingIds.has(id)) return false; existingIds.add(id); return true; });
				if (add.length) {
					addedAny = true;
					addedPosts.push(...add);
					setPosts(prev => mergeUniquePosts(prev, add, false));
				}

				// If we added items, break; otherwise, if the server returned a full page and we haven't exceeded attempts, fetch next page and continue
				// Use `add.length` (items actually added) as the trigger to stop paging —
				// `publicPosts.length` can be >0 but all items may be duplicates, causing premature stop.
				if ((add.length > 0) || arr.length < PAGE_SIZE || attempts >= maxAttempts) {
					// update skip and hasMore based on cumulative server results
					const newSkip = localBaseSkip + cumulativeArrCount;
					setSkipCount(newSkip);
					skipRef.current = newSkip;
					if (total !== null) {
						setHasMorePosts(newSkip < total);
						hasMoreRef.current = newSkip < total;
					} else {
						const hm = (arr.length === PAGE_SIZE || addedAny);
						setHasMorePosts(hm);
						hasMoreRef.current = hm;
					}
					// fetch counts only for the newly added posts
					console.debug('loadPublicPosts: page results', { baseSkip, cumulativeArrCount, newSkip: (localBaseSkip + cumulativeArrCount), total, added: addedPosts.length });
					if (addedPosts.length) try { fetchCountsForPosts(addedPosts).catch(err => console.error('Erro ao buscar counts', err)); } catch (e) {}
					break;
							} else {
								// fetch next page from server
							localBaseSkip += arr.length;
					try {
						const nextEndpoint = (sortOrder === 'asc') ? `Postagem/antigo?skip=${localBaseSkip}&take=${PAGE_SIZE}` : `Postagem?skip=${localBaseSkip}&take=${PAGE_SIZE}`;
						if (dateFilter) nextEndpoint += `&date=${dateFilter}`;
						console.debug('loadPublicPosts: fetching next page for skip', localBaseSkip, 'endpoint', nextEndpoint);
						const nextData = await apiCall(nextEndpoint, 'GET');
						arrLocal = (nextData && nextData.Items) ? nextData.Items : (Array.isArray(nextData) ? nextData : (nextData?.items || []));
						if (total === null && nextData && typeof nextData.Total === 'number') total = nextData.Total;
					} catch (e) {
						console.warn('Erro ao buscar próxima página para pular itens filtrados', e);
						break;
					}
				}
			}

			} catch (err) {
			console.error('Erro ao carregar postagens públicas', err);
			setPostsError('Erro ao carregar publicações');
		} finally {
				setPostsLoading(false);
				setLoadingMore(false);
				loadingMoreRef.current = false;
		}
	};

	// On mount and when filters change, reset pagination and load first page; also poll periodically
	useEffect(() => {
		let mounted = true;
		loadPublicPosts({ reset: true });
		const refreshLatest = async () => {
			if (refreshingLatest) return;
			setRefreshingLatest(true);
			try {
				const endpointBase = (sortOrder === 'asc') ? `Postagem/antigo?skip=0&take=${PAGE_SIZE}` : `Postagem?skip=0&take=${PAGE_SIZE}`;
				let endpoint = endpointBase;
				if (dateFilter) endpoint += `&date=${dateFilter}`;
				const data = await apiCall(endpoint, 'GET');
				const arr = (data && data.Items) ? data.Items : (Array.isArray(data) ? data : (data?.items || []));
				if (!arr || !arr.length) return;
				// find items that are not in current posts
				setPosts(prev => {
					const existingIds = new Set((prev || []).map(x => x.Id || x.id || x.PostagemId || x.postagemId));
					const newItems = (arr || []).filter(i => {
						const id = i.Id || i.id || i.PostagemId || i.postagemId;
						if (!id || existingIds.has(id)) return false;
						const gid = i.GrupoId || i.grupoId;
						if (gid) return myGroupIdsRef.current.has(gid) || myGroupIdsRef.current.has(String(gid));
						if (i.Grupo) return false;
						if (i.Grupos && Array.isArray(i.Grupos) && i.Grupos.length) return false;
						return true;
					});
					if (!newItems.length) return prev;
					// enrich fetched items and then insert depending on sortOrder
					Promise.all(newItems.map(n => fetchFilesForPost(n))).then(enriched => {
						if (sortOrder === 'asc') {
							// oldest-first view: append newer items at the bottom
							setPosts(curr => mergeUniquePosts(curr, enriched, false));
							// no change to skipCount needed
						} else {
							// newest-first: prepend new items and adjust skipCount so pagination remains aligned
							setPosts(curr => mergeUniquePosts(curr, enriched, true));
							setSkipCount(prev => {
								const v = (prev || 0) + enriched.length;
								skipRef.current = v;
								return v;
							});
						}
						fetchCountsForPosts(enriched).catch(() => {});
					}).catch(() => {});
					return prev;
				});
			} catch (e) {
				console.warn('Erro ao refrescar posts', e);
			} finally {
				setRefreshingLatest(false);
			}
		};

		const t = setInterval(() => {
			if (!mounted) return;
			refreshLatest();
		}, 30000);
		return () => { mounted = false; clearInterval(t); };
	}, [sortOrder, dateFilter]);

	// Use IntersectionObserver on a sentinel element to reliably trigger loading more posts
	useEffect(() => {
		if (!bottomRef) return;
		const el = bottomRef.current;
		if (!el) return;
		const obs = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					if (!hasMoreRef.current || loadingMoreRef.current || postsLoading) return;
					// call loadPublicPosts; do not await here
					loadPublicPosts({ reset: false }).catch(err => console.debug('loadPublicPosts failed from observer', err));
				}
			});
		}, { root: null, rootMargin: '400px', threshold: 0 });
		obs.observe(el);
		return () => { try { obs.disconnect(); } catch (e) {} };
	}, [postsLoading]);

	// cleanup post object URLs on unmount
	useEffect(() => {
		return () => {
			try {
				Object.values(postObjectUrlRefs.current || {}).forEach(arr => { (arr || []).forEach(u => { try { URL.revokeObjectURL(u); } catch {} }); });
			} catch (e) {}
		};
	}, []);

	useEffect(() => {
		let mounted = true;
		loadSidebarGroups();
		loadMinhasConexoes();
		
		// Listener para atualizar grupos quando um novo for criado
		const handleGroupsChanged = () => {
			if (mounted) loadSidebarGroups();
		};
		window.addEventListener('groupsChanged', handleGroupsChanged);
		
		// Listener para atualizar conexões
		const handleConexoesChanged = () => {
			if (mounted) loadMinhasConexoes();
		};
		window.addEventListener('conexoesChanged', handleConexoesChanged);
		
		return () => { 
			mounted = false; 
			window.removeEventListener('groupsChanged', handleGroupsChanged);
			window.removeEventListener('conexoesChanged', handleConexoesChanged);
		};
	}, []);

	// All composer-related inputs/handlers moved to FormularioPostagem

	const sortedPosts = useMemo(() => {
		let copy = posts.slice();

		// If sorting by date (filter mode), and a date is selected, filter posts to that day
		if (sortOrder === 'date' && dateFilter) {
			// normalize selected date (YYYY-MM-DD)
			const selectedYmd = dateFilter; // input type=date already gives YYYY-MM-DD
			const ymdOf = (raw) => {
				if (!raw) return null;
				const d = new Date(raw);
				if (isNaN(d.getTime())) return null;
				const y = d.getFullYear();
				const m = (d.getMonth() + 1).toString().padStart(2, '0');
				const day = d.getDate().toString().padStart(2, '0');
				return `${y}-${m}-${day}`;
			};
			copy = copy.filter(p => {
				const raw = p.createdAt || p.DataHora || p.dataHora || p.DataCriacao || p.dataCriacao || p.created_at || p.Data || p.DataPublicacao;
				const ymd = ymdOf(raw);
				return ymd === selectedYmd;
			});
			// keep newest first inside that day
			copy.sort((a, b) => new Date(b.createdAt || b.DataHora || b.dataHora || b.DataCriacao || b.dataCriacao || b.created_at || 0) - new Date(a.createdAt || a.DataHora || a.dataHora || a.DataCriacao || a.dataCriacao || a.created_at || 0));
			return copy;
		}

		// Normal sorting: newest or oldest
		copy.sort((a, b) => {
			const da = new Date(a.createdAt);
			const db = new Date(b.createdAt);
			return sortOrder === 'desc' ? db - da : da - db;
		});
		return copy;
	}, [posts, sortOrder, dateFilter]);

	// click outside behavior is handled inside FormularioPostagem for emoji dropdown

	// Agendamentos: fetch real data from API and compute overdue items (max 4)
	const [agendamentos, setAgendamentos] = React.useState([]);
	const [agendamentosLoading, setAgendamentosLoading] = React.useState(false);
	const [versaoSistema, setVersaoSistema] = React.useState('');

	React.useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				const data = await apiCall('VersaoSistema', 'GET');
				if (mounted) setVersaoSistema(data?.dataHora || data?.DataHora || '');
			} catch (error) {
				console.error('Erro ao carregar versão do sistema', error);
			}
		})();
		return () => { mounted = false; };
	}, []);

	React.useEffect(() => {
		let mounted = true;
		setAgendamentosLoading(true);
		(async () => {
			try {
				const data = await apiCall('Agendamentos', 'GET');
				const arr = Array.isArray(data) ? data : (data?.Agendamentos || data?.agendamentos || []);
				if (!mounted) return;
				// try to resolve client names when agendamentos include client ids
				const list = arr || [];
				const clientIds = Array.from(new Set(list.map(a => (a.ClienteId || a.clienteId || a.Id_cliente || a.IdCliente || a.Cliente || a.idCliente || null)).filter(Boolean)));
				let clientsMap = {};
				let clientsArr = [];
				if (clientIds.length) {
					try {
						const clientsData = await apiCall('Cliente', 'GET');
						const clientsArr = Array.isArray(clientsData) ? clientsData : (clientsData?.Clientes || clientsData?.clientes || []);
						clientsArr = clientsArr || clientsArr;
						clientsMap = {};
						(clientsArr || []).forEach(c => {
							const name = c.Nome || c.nome || c.NomeCliente || c.ClienteNome || c.name || '';
							// collect several possible id keys used by different APIs
							const ids = [c.Id, c.id, c.ClienteId, c.clienteId, c.Id_cliente, c.IdCliente, c.Cliente];
							ids.forEach(i => { if (i != null) clientsMap[String(i)] = name; });
						});
					} catch (e) {
						console.warn('Não foi possível obter clientes para mapear nomes', e);
					}
				}
				// attach clienteNome when possible (backend now includes Cliente object)
				const withNames = list.map(a => {
					const clienteObj = a.Cliente || a.cliente;
					let nome = '';
					// Extract from Cliente navigation property if object
					if (clienteObj && typeof clienteObj === 'object') {
						nome = clienteObj.Nome || clienteObj.nome || clienteObj.NomeCliente || clienteObj.name || '';
					}
					if (!nome) {
						nome = a.clienteNome || a.ClienteNome || a.NomeCliente || a.Nome || a.nome || '';
					}
					if (!nome) {
						const cidCandidates = [a.ClienteId, a.clienteId, a.Id_cliente, a.IdCliente, a.idCliente, a.Id, a.id];
						for (const cval of cidCandidates) {
							if (cval == null) continue;
							const mapped = clientsMap[String(cval)];
							if (mapped) { nome = mapped; break; }
						}
					}
					return { ...a, clienteNome: nome };
				});
				setAgendamentos(withNames);
				// expose debug variables to help inspect returned data in browser console
				try {
					if (typeof window !== 'undefined') {
						window.__debug_agendamentos = withNames;
						window.__debug_clients = clientsArr || [];
						console.debug('DEBUG: __debug_agendamentos and __debug_clients set on window');
					}
				} catch (e) {}
			} catch (err) {
				console.error('Erro ao carregar agendamentos (sidebar)', err);
				if (mounted) setAgendamentos([]);
			} finally {
				if (mounted) setAgendamentosLoading(false);
			}
		})();
		return () => { mounted = false; };
	}, []);

	const agora = new Date();

	// helper to extract date/time field for an agendamento (supports common shapes)
	const getAgendaDate = (a) => {
		if (!a) return null;
		return a.dataPrevista || a.dataHora || a.DataHora || a.data || a.Data || a.dataAgendada || null;
	};

	const getClienteNome = (a) => {
		if (!a) return '';
		// Backend now includes Cliente navigation property - extract name from it if object
		const clienteObj = a.Cliente || a.cliente;
		if (clienteObj && typeof clienteObj === 'object') {
			const nameFromObj = clienteObj.Nome || clienteObj.nome || clienteObj.NomeCliente || clienteObj.name || '';
			if (nameFromObj && String(nameFromObj).trim()) return nameFromObj;
		}
		const nome = a.clienteNome || a.ClienteNome || a.NomeCliente || a.nome || a.Nome || '';
		if (nome && String(nome).trim()) return nome;
		// fallback to client id if available
		const cid = a.ClienteId || a.clienteId || a.Id_cliente || a.IdCliente || a.idCliente || a.Id || a.id;
		if (cid) return `Cliente ${cid}`;
		return '';
	};

	const overdueAll = (agendamentos || [])
		.map(a => ({
			_original: a,
			date: new Date(getAgendaDate(a) || a.DataHora || a.dataPrevista || a.data || null),
		}))
		.filter(x => {
			const status = String(x._original.Status || x._original.status || '')
				.trim()
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase();
			return x.date && !isNaN(x.date.getTime()) && x.date < agora && status !== 'concluido' && status !== 'cancelado';
		})
		.sort((a, b) => b.date - a.date); // most recently missed first

	const overdue = overdueAll.slice(0, 4).map(x => ({ ...x._original, _dateObj: x.date }));

	return (
		<div className="home-root">
			{lightboxOpen && (
				<Lightbox images={lbImages} index={lbIndex} onClose={closeLightbox} onNext={lbNext} onPrev={lbPrev} />
			)}
			<aside className="sidebar-left">
				<div style={{ padding: '8px 6px' }}>
					<div className="item destaque" role="button" tabIndex={0} title="Destaques da empresa">Destaques da empresa</div>
					<div style={{ height: 12 }} />
					{/* Forum Empresas */}
					<div
						className="item grupo"
						role="button"
						tabIndex={0}
						title="Forum Empresas"
						onClick={() => navigate('/sidebar/ForumEmpresas')}
						onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/sidebar/ForumEmpresas'); }}
						style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
					>
						<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, opacity: 0.85 }}>
							<rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" fill="none"/>
							<line x1="7" y1="8" x2="17" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
							<line x1="7" y1="12" x2="17" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
							<line x1="7" y1="16" x2="13" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
						</svg>
						<span>Forum Empresas</span>
					</div>
					<div style={{ height: 12 }} />
					<div className="item grupo" role="button" tabIndex={0} title="Grupos">Grupos</div>
					<div style={{ height: 8 }} />
					{/* Sidebar static group listing (auto-loaded) */}
					{sidebarGroupsLoading && <div style={{ marginTop: 12, color: '#666' }}>Carregando grupos...</div>}
					{sidebarGroupsError && <div style={{ marginTop: 12, color: '#d32f2f' }}>{sidebarGroupsError}</div>}
						{!sidebarGroupsLoading && sidebarGroups && sidebarGroups.length > 0 && (
							<div className="sidebar-groups-list" style={{ marginTop: 12 }}>
								{sidebarGroups.slice(0,4).map((g, i) => (
							<div key={g.Id || g.id || i} className="sidebar-groups-item" onClick={() => { 
								// navigate to the standalone GrupoDropDown route and pass the selected group in location state
								navigate('/sidebar/GrupoDropDown', { state: { selectedGroup: g } });
							}}>
									{g.NomeGrupo || g.nomeGrupo || g.Nome || g.nome}
								</div>
							))}
							{sidebarGroups.length > 5 && (
								<button className="sidebar-groups-item ver-todos" onClick={() => setShowAllGroups(true)}>Ver todos ({sidebarGroups.length})</button>
							)}
						</div>
					)}
					<div style={{ height: 8 }} />
					{/* Create group button as last item */}
					<button className="create-group-btn" onClick={() => setShowCreateGroup(true)} aria-label="Criar grupo">Criar grupo</button>
					
					{/* Seção Minhas Conexões */}
					<div style={{ height: 16 }} />
					<div className="item destaque" role="button" tabIndex={0} title="Minhas Conexões">Minhas Conexões</div>
					<div style={{ height: 8 }} />
					
					{conexoesLoading && <div style={{ color: '#666', fontSize: '13px' }}>Carregando...</div>}
					
					{!conexoesLoading && minhasConexoes.length === 0 && (
						<div style={{ color: '#888', fontSize: '13px', fontStyle: 'italic' }}>Nenhuma conexão</div>
					)}
					
					{!conexoesLoading && minhasConexoes.length > 0 && (
						<div className="sidebar-conexoes-list" style={{ marginLeft: '-5px' }}>
							{minhasConexoes.slice(0, 5).map((u, i) => {
								const nome = u.nome || '';
								const login = u.login || '';
								const uid = u.id;
								// Pegar primeira letra do primeiro nome
								const primeiroNome = nome.split(' ')[0] || '';
								const inicial = primeiroNome.charAt(0).toUpperCase() || '?';
								return (
									<div 
										key={uid || i} 
										className="sidebar-conexao-item"
										onClick={() => navigate('/sidebar/Funcionario', { state: { funcionario: { id: uid, Id: uid, login: login, Login: login, nome: nome } } })}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '10px',
											padding: '8px 8px',
											borderRadius: '6px',
											cursor: 'pointer',
											transition: 'background 0.2s',
											width: '100%'
										}}
										onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(235, 133, 0,0.15)'}
										onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
									>
										{userPhotos[uid] ? (
											<div style={{
												width: '36px',
												height: '36px',
												borderRadius: '50%',
												overflow: 'hidden',
												flexShrink: 0
											}}>
												<img src={userPhotos[uid]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
											</div>
										) : (
											<div style={{
												width: '36px',
												height: '36px',
												borderRadius: '50%',
												backgroundColor: 'rgb(235, 133, 0)',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												color: 'white',
												fontWeight: 'bold',
												fontSize: '15px',
												flexShrink: 0
											}}>
												{inicial}
											</div>
										)}
										<div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
											<div style={{ 
												color: '#333', 
												fontSize: '14px', 
												fontWeight: '500',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												maxWidth: '100%'
											}}>
												{nome}
											</div>
											<div style={{ 
												color: '#888', 
												fontSize: '12px',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												maxWidth: '100%'
											}}>
												{login}
											</div>
										</div>
									</div>
								);
							})}
							<button 
								className="sidebar-conexoes-ver-todos"
								onClick={() => navigate('/minhas-conexoes')}
								style={{
									width: '100%',
									padding: '10px',
									marginTop: '8px',
									backgroundColor: 'transparent',
									border: '1px solid rgb(235, 133, 0)',
									borderRadius: '6px',
									color: 'rgb(235, 133, 0)',
									fontSize: '13px',
									fontWeight: '500',
									cursor: 'pointer'
								}}
							>
								Ver todos ({minhasConexoes.length})
							</button>
						</div>
					)}
				</div>


			</aside>
			{showCreateGroup && (
				<FormularioGrupo onClose={() => setShowCreateGroup(false)} onCreated={async (g) => { try {
					await loadSidebarGroups();
				} catch (err) {
					console.error('Erro ao atualizar lista de grupos após criação', err);
				} }} apiCall={apiCall} />
			)}
			{showGroupsDropdown && (
			<GroupsModal 
				onClose={() => setShowGroupsDropdown(false)}
				onSelect={(selected, flags) => {
					if (selected && selected.length > 0) setSelectedGroup(selected[0]);
					setShowGroupsDropdown(false);
				}}
				apiCall={apiCall}
				initialSelected={selectedGroup ? [selectedGroup] : []}
			/>
		)}

		{showAllGroups && (
			<AllGroupsModal onClose={() => setShowAllGroups(false)} apiCall={apiCall} />
		)}
			<main className="main-center">
				{/* ── Barra de pesquisa global ── */}
				<div className="home-search-wrapper" ref={searchWrapperRef} style={{ marginTop: 22 }}>
					<div className="home-search-input-container">
						<input
							type="text"
							className="home-search-input"
							placeholder="Pesquisar clientes ou funcionários..."
							value={searchTerm}
							onChange={handleSearchChange}
							onFocus={() => { if (searchResults.length > 0) setSearchOpen(true); }}
						/>
						<span className="home-search-icon">
							{searchLoading ? (
								<svg width="20" height="20" viewBox="0 0 50 50" style={{ animation: 'spin 1s linear infinite' }}>
									<circle cx="25" cy="25" r="20" stroke="rgb(235, 133, 0)" strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray="31.4 31.4" />
								</svg>
							) : (
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgb(235, 133, 0)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
									<circle cx="11" cy="11" r="7" />
									<line x1="21" y1="21" x2="16.65" y2="16.65" />
								</svg>
							)}
						</span>
					</div>
					{searchOpen && searchResults.length > 0 && (
						<div className="home-search-dropdown">
							{searchResults.map((item, idx) => (
								<div key={`${item.tipo}-${item.id}-${idx}`} className="home-search-item" onClick={() => handleSearchResultClick(item)}>
									<div className="home-search-item-nome">
										{item.tipo === 'funcionario' && userPhotos[item.id] ? (
											<img src={userPhotos[item.id]} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', marginRight: 8, verticalAlign: 'middle', flexShrink: 0 }} />
										) : (
											<span style={{
												display: 'inline-flex', width: 24, height: 24, borderRadius: '50%',
												background: 'linear-gradient(135deg, rgb(235, 133, 0) 0%, rgb(184, 98, 0) 100%)',
												alignItems: 'center', justifyContent: 'center',
												color: 'white', fontWeight: 'bold', fontSize: '12px',
												marginRight: 8, verticalAlign: 'middle', flexShrink: 0
											}}>
												{(item.nome || '?').charAt(0).toUpperCase()}
											</span>
										)}
										{item.nome}
									</div>
									<div className="home-search-item-bottom">
										<span className={`home-search-item-tipo ${item.tipo === 'cliente' ? 'tipo-cliente' : 'tipo-funcionario'}`}>
											{item.tipo === 'cliente' ? 'Cliente' : 'Funcionário'}
										</span>
										{item.tipo === 'cliente' && item.cpf && (
											<span className="home-search-item-sub-inline" style={{ color: '#1976d2' }}>{item.cpf}</span>
										)}
										{item.tipo === 'funcionario' && item.login && (
											<>
												<span className="home-search-item-sub-inline" style={{ color: 'rgb(235, 133, 0)' }}>{item.login}</span>
												{item.isConnected && (
													<>
														<span className="home-search-connected-badge" title="Conectado">
															<svg width="14" height="14" viewBox="0 0 24 24" fill="#4caf50" xmlns="http://www.w3.org/2000/svg">
																<path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
															</svg>
														</span>
														<span className="home-search-connected-text">Conectado</span>
													</>
												)}
											</>
										)}
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				<div className="feed-card">
					<FormularioPostagem apiCall={apiCall} apiUploadMultipart={apiUploadMultipart} onPosted={(p) => { (async () => {
						setPostingGlobal(true);
						try {
							const enriched = await fetchFilesForPost(p);
							const item = { ...enriched, createdAt: extractPostDate(p) };
							setPosts(prev => mergeUniquePosts(prev, [item], true));
							fetchCountsForPosts([item]).catch(err => console.error('Erro ao buscar counts do novo post', err));
						} catch (e) {
							const item = { ...p, createdAt: extractPostDate(p) };
							setPosts(prev => mergeUniquePosts(prev, [item], true));
							fetchCountsForPosts([item]).catch(err => console.error('Erro ao buscar counts do novo post', err));
						} finally {
							setPostingGlobal(false);
						}
					})(); }} />
				</div>
				{postingGlobal && (
					<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<div style={{ background: '#fff', borderRadius: 12, padding: '28px 40px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
							<div style={{ width: 36, height: 36, border: '4px solid #e0e0e0', borderTop: '4px solid rgb(235, 133, 0)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
							<span style={{ fontWeight: 600, color: '#333', fontSize: 15 }}>Publicando...</span>
						</div>
					</div>
				)}
				{/* Sort select visible under the feed-card, centered and separate */}
				<div className="sort-container">
					<label style={{ fontSize: 13, color: '#666', marginRight: 8, display: 'inline-flex', alignItems: 'center' }} htmlFor="sort-posts">Ordenar:</label>
					<select id="sort-posts" className="sort-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} aria-label="Ordenar posts">
						<option value="desc">Mais recente</option>
						<option value="asc">Mais antigo</option>
						<option value="date">Por data</option>
					</select>
					{sortOrder === 'date' && (
						<div className="date-filter">
							<input ref={dateInputRef} type="date" className="date-input" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} aria-label="Filtrar por data" />
						</div>
					)}
				</div>

<div style={{ textAlign: 'center', marginTop: 12 }}>
				<h3 style={{ margin: 0, fontSize: 18 }}>Publicações públicas {postsLoading ? '(Carregando...)' : `(${sortedPosts.length})`}</h3>
				{!postsLoading && sortedPosts.length === 0 && (
					<div style={{ color: '#666', marginTop: 8 }}>Nenhuma publicação pública encontrada.</div>
				)}
			</div>

			<div className="post-list">
					{sortedPosts.map((p, idx) => {
					// Buscar nome do usuário se ainda não temos
					const postUserId = p.UsuarioSistemaId || p.usuarioSistemaId || p.UsuarioSistema?.Id || p.usuarioSistema?.id;
					if (postUserId && !userNamesById[postUserId] && !userNamesFetchingRef.current.has(postUserId)) {
						fetchUserName(postUserId);
					}
					const postUserName = getUserName(postUserId);
				const postGrupoId = p.GrupoId || p.grupoId;
				const postGrupoNome = postGrupoId ? (myGroupNamesRef.current[postGrupoId] || myGroupNamesRef.current[String(postGrupoId)] || '') : '';
				return (
				<div className={`post-card${postGrupoId ? ' post-card-grupo' : ''}`} key={p.Id || p.id || p.PostagemId || p.postagemId || p.createdAt || idx} style={{ position: 'relative' }}>
					{/* Trash + Pencil icons — only for posts owned by the logged user */}
					{(() => { const myId = getUsuarioSistemaId(); const ownerId = p.UsuarioSistemaId || p.usuarioSistemaId || p.UsuarioSistema?.Id || p.usuarioSistema?.id; return myId && ownerId && String(myId) === String(ownerId); })() && editingPostId !== (p.Id || p.id) && (
						<div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 4, zIndex: 2 }}>
							<button type="button" title="Excluir postagem" aria-label="Excluir postagem" onClick={() => setConfirmDeletePostId(p.Id || p.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 1 }}>
								<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#e53935" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
							</button>
							<button type="button" title="Editar postagem" aria-label="Editar postagem" onClick={() => startEditPost(p)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, lineHeight: 1 }}>
								<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="rgb(235, 133, 0)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
							</button>
						</div>
					)}
					{/* Nome do usuário que fez a postagem e data/hora */}
					<div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: postGrupoNome ? 2 : 6 }}>
						{postUserName && <div className="post-author" style={{ fontWeight: 'bold', color: '#333' }}>{postUserName}</div>}
						<div className="post-time">{formatPostDate(p.createdAt)}</div>
					</div>
					{/* Nome do grupo em azul escuro (só para postagens de grupo) */}
					{postGrupoNome && (
						<div style={{ fontSize: 13, color: '#1a237e', fontWeight: 600, marginBottom: 6 }}>{postGrupoNome}</div>
					)}
					{(p.Titulo || p.title || p.titulo) && <div className="post-title">{p.Titulo || p.title || p.titulo}</div>}

					{/* description or edit mode */}
					{editingPostId === (p.Id || p.id) ? (
						<div style={{ marginBottom: 8 }}>
							<textarea value={editingText} onChange={(e) => setEditingText(e.target.value)} style={{ width: '100%', minHeight: 80, padding: 8, borderRadius: 6, border: '1px solid #ddd', fontSize: 14, resize: 'vertical' }} disabled={editingSaving} />

							{/* === Edit-mode image thumbnails === */}
							{(editImages.length > 0 || true) && (
								<div style={{ marginTop: 10 }}>
									<div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>Imagens</div>
									<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
										{editImages.map((img, i) => (
											<div key={img.id || img._tempUrl || i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 6, overflow: 'hidden', border: '1px solid #ddd' }}>
												{img.type && img.type.startsWith('video/') ? (
													<video src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
												) : (
													<img src={img.url} alt={img.name || `img-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
												)}
												<button type="button" onClick={() => editRemoveImage(i)} style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, lineHeight: 1 }} title="Remover imagem">
													<svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="2" y1="2" x2="10" y2="10"/><line x1="10" y1="2" x2="2" y2="10"/></svg>
												</button>
											</div>
										))}
										{/* Add image button */}
										<div onClick={() => editImageInputRef.current && editImageInputRef.current.click()} style={{ width: 64, height: 64, borderRadius: 6, border: '2px dashed rgb(235, 133, 0)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fff' }} title="Adicionar imagem">
											<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgb(235, 133, 0)" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
										</div>
										<input ref={editImageInputRef} type="file" accept="image/*,video/*" multiple style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files.length > 0) { editAddImages(e.target.files); e.target.value = ''; } }} />
									</div>
									{/* Ver todos button */}
									{(editImages.length + editFiles.length) > 0 && (
										<div style={{ textAlign: 'center', marginTop: 10 }}>
											<button type="button" onClick={() => { setShowVerTodos(true); setVerTodosSelectedImages(new Set()); setVerTodosSelectedFiles(new Set()); setConfirmBulkDelete(false); }} style={{ background: 'transparent', border: 'none', color: 'rgb(235, 133, 0)', fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: '4px 16px' }}>Ver todos</button>
										</div>
									)}
								</div>
							)}

							{/* === Edit-mode non-image files (PDF, TXT, etc.) === */}
							{(editFiles.length > 0 || true) && (
								<div style={{ marginTop: 12 }}>
									<div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6 }}>Arquivos</div>
									<div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
										{editFiles.map((f, i) => {
											const fname = f.NomeArquivo || f.nome || f.Nome || f.fileName || f.name || `Arquivo ${i+1}`;
											const t = (f.type || '').toLowerCase();
											const n = (fname).toLowerCase();
											const emoji = (t.includes('pdf') || n.endsWith('.pdf')) ? '📄' : (t.includes('text') || n.endsWith('.txt')) ? '📝' : n.endsWith('.csv') ? '📑' : '📎';
											return (
												<div key={f.id || f._tempUrl || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: '#f9f9f9', borderRadius: 6, border: '1px solid #eee', position: 'relative' }}>
													<span style={{ fontSize: 16 }}>{emoji}</span>
													<span style={{ flex: 1, fontSize: 13, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fname}</span>
													<button type="button" onClick={() => editRemoveFile(i)} style={{ background: 'rgba(0,0,0,0.55)', border: 'none', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0 }} title="Remover arquivo">
														<svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="2" y1="2" x2="10" y2="10"/><line x1="10" y1="2" x2="2" y2="10"/></svg>
													</button>
												</div>
											);
										})}
										{/* Add file button */}
										<div onClick={() => editFileInputRef.current && editFileInputRef.current.click()} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 6, border: '2px dashed rgb(235, 133, 0)', cursor: 'pointer', background: '#fff', justifyContent: 'center' }} title="Adicionar arquivo">
											<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(235, 133, 0)" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
											<span style={{ color: 'rgb(235, 133, 0)', fontWeight: 600, fontSize: 13 }}>Adicionar arquivo</span>
										</div>
										<input ref={editFileInputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => { if (e.target.files && e.target.files.length > 0) { editAddFiles(e.target.files); e.target.value = ''; } }} />
									</div>
								</div>
							)}

							{/* Save / Cancel at the bottom right, below everything */}
							<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
								<button onClick={cancelEdit} className="button-create" style={{ background: '#9e9e9e', fontSize: 13, padding: '6px 14px' }} disabled={editingSaving}>Cancelar</button>
								<button onClick={() => saveEditPost(p.Id || p.id)} className="button-create" style={{ fontSize: 13, padding: '6px 14px' }} disabled={editingSaving}>{editingSaving ? 'Salvando...' : 'Salvar'}</button>
							</div>

							{/* "Ver todos" popup */}
							{showVerTodos && (
								<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => { setShowVerTodos(false); setConfirmBulkDelete(false); }}>
									<div style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', width: '90%', maxWidth: 560, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
										{/* Images section */}
										<div style={{ marginBottom: 18 }}>
											<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
												<input type="checkbox" checked={editImages.length > 0 && verTodosSelectedImages.size === editImages.length} onChange={(e) => {
													if (e.target.checked) setVerTodosSelectedImages(new Set(editImages.map((_, i) => i)));
													else setVerTodosSelectedImages(new Set());
												}} style={{ width: 18, height: 18, accentColor: 'rgb(235, 133, 0)' }} />
												<span style={{ fontWeight: 700, fontSize: 15 }}>Imagens ({editImages.length})</span>
											</div>
											{editImages.length === 0 ? (
												<div style={{ color: '#999', fontSize: 13, paddingLeft: 26 }}>Nenhuma imagem</div>
											) : (
												<div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, paddingLeft: 4 }}>
													{editImages.map((img, i) => (
														<div key={img.id || img._tempUrl || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
															<input type="checkbox" checked={verTodosSelectedImages.has(i)} onChange={() => {
																setVerTodosSelectedImages(prev => {
																	const next = new Set(prev);
																	next.has(i) ? next.delete(i) : next.add(i);
																	return next;
																});
															}} style={{ width: 16, height: 16, accentColor: 'rgb(235, 133, 0)', marginTop: 4 }} />
															<div style={{ width: 72, height: 72, borderRadius: 6, overflow: 'hidden', border: verTodosSelectedImages.has(i) ? '2px solid rgb(235, 133, 0)' : '1px solid #ddd' }}>
																{img.type && img.type.startsWith('video/') ? (
																	<video src={img.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
																) : (
																	<img src={img.url} alt={img.name || `img-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
																)}
															</div>
														</div>
													))}
												</div>
											)}
										</div>
										{/* Separator */}
										<div style={{ borderTop: '1px solid #eee', margin: '12px 0' }} />
										{/* Files section */}
										<div style={{ marginBottom: 16 }}>
											<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
												<input type="checkbox" checked={editFiles.length > 0 && verTodosSelectedFiles.size === editFiles.length} onChange={(e) => {
													if (e.target.checked) setVerTodosSelectedFiles(new Set(editFiles.map((_, i) => i)));
													else setVerTodosSelectedFiles(new Set());
												}} style={{ width: 18, height: 18, accentColor: 'rgb(235, 133, 0)' }} />
												<span style={{ fontWeight: 700, fontSize: 15 }}>Arquivos ({editFiles.length})</span>
											</div>
											{editFiles.length === 0 ? (
												<div style={{ color: '#999', fontSize: 13, paddingLeft: 26 }}>Nenhum arquivo</div>
											) : (
												<div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 4 }}>
													{editFiles.map((f, i) => {
														const fname = f.NomeArquivo || f.nome || f.Nome || f.fileName || f.name || `Arquivo ${i+1}`;
														const t = (f.type || '').toLowerCase();
														const n = fname.toLowerCase();
														const emoji = (t.includes('pdf') || n.endsWith('.pdf')) ? '📄' : (t.includes('text') || n.endsWith('.txt')) ? '📝' : n.endsWith('.csv') ? '📑' : '📎';
														return (
															<div key={f.id || f._tempUrl || i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
																<input type="checkbox" checked={verTodosSelectedFiles.has(i)} onChange={() => {
																	setVerTodosSelectedFiles(prev => {
																		const next = new Set(prev);
																		next.has(i) ? next.delete(i) : next.add(i);
																		return next;
																	});
																}} style={{ width: 16, height: 16, accentColor: 'rgb(235, 133, 0)' }} />
																<span style={{ fontSize: 16 }}>{emoji}</span>
																<span style={{ fontSize: 13, color: '#333' }}>{fname}</span>
															</div>
														);
													})}
												</div>
											)}
										</div>
										{/* Buttons */}
										<div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
											<button className="button-create" style={{ background: '#9e9e9e', fontSize: 14, padding: '8px 18px' }} onClick={() => { setShowVerTodos(false); setConfirmBulkDelete(false); }}>Cancelar</button>
											<button className="button-create" style={{ background: '#d32f2f', fontSize: 14, padding: '8px 18px' }} disabled={verTodosSelectedImages.size === 0 && verTodosSelectedFiles.size === 0} onClick={() => setConfirmBulkDelete(true)}>Deletar</button>
										</div>
										{/* Bulk delete confirmation */}
										{confirmBulkDelete && (
											<div style={{ marginTop: 14, padding: '14px 16px', background: '#fff3f3', borderRadius: 8, border: '1px solid #ffcdd2' }}>
												<div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Confirmar exclusão?</div>
												<div style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
													{verTodosSelectedImages.size > 0 && <span>{verTodosSelectedImages.size} imagem(ns) </span>}
													{verTodosSelectedImages.size > 0 && verTodosSelectedFiles.size > 0 && <span>e </span>}
													{verTodosSelectedFiles.size > 0 && <span>{verTodosSelectedFiles.size} arquivo(s) </span>}
													serão removidos. Esta ação não pode ser desfeita.
												</div>
												<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
													<button className="button-create" style={{ background: '#9e9e9e', fontSize: 13, padding: '6px 14px' }} onClick={() => setConfirmBulkDelete(false)}>Cancelar</button>
													<button className="button-create" style={{ background: '#d32f2f', fontSize: 13, padding: '6px 14px' }} onClick={bulkDeleteFiles}>Confirmar</button>
												</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					) : (
						<>
					{(p.Descricao || p.descricao || p.description) && (
						<div className="post-description">{p.Descricao || p.descricao || p.description}</div>
					)}

					{/* gallery when there are image files (only images/videos, PDFs stay in the non-image block) */}
					{p.files && p.files.length > 0 && (p.files.some(f => f.type && (f.type.startsWith('image/') || f.type.startsWith('video/')))) ? (
						<div>
				{(() => {
					const images = (p.files || []).filter(f => f && f.type && (f.type.startsWith('image/') || f.type.startsWith('video/')));
					if (!images || images.length === 0) return null;
					const idx = (imageIndexes[p.Id || p.id] || 0);
					const cf = images[idx] || {};
					return (
						<div className={`post-image-wrap ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`}>
							{images.length > 1 && (
								<div className="post-image-arrow left" role="button" title="Imagem anterior" onClick={() => prevImage(p.Id || p.id)}>◀</div>
							)}
								{cf && cf.url ? (
								(cf.type && cf.type.startsWith('video/')) ? (
									<div className="post-video-wrap"><video src={cf.url} className={`post-video ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`} controls /></div>
								) : (
									<img className={`post-main-image ${cf.isSquare ? 'square' : cf.isPortrait ? 'portrait' : ''}`} src={cf.url} alt="Post image" loading="lazy" onClick={() => {
										// open lightbox only for the main image
										const imgs = images.map(f => ({ url: f.url, type: f.type }));
										openLightbox(imgs, idx);
									}} onError={(e) => { e.target.style.display = 'none'; }} />
								)
							) : null}
							{images.length > 1 && (
								<div className="post-image-arrow right" role="button" title="Próxima imagem" onClick={() => nextImage(p.Id || p.id)}>▶</div>
							)}
							{images.length > 1 && (
								<div className="post-image-count">{(idx + 1)} / {images.length}</div>
							)}
						</div>
					);
				})()}

							<div className="post-thumbs">
								{(() => {
									const images = (p.files || []).filter(f => f && f.type && (f.type.startsWith('image/') || f.type.startsWith('video/')));
									return images.map((f, idx) => (
										<div key={idx} className={`post-thumb ${(imageIndexes[p.Id || p.id] || 0) === idx ? 'active' : ''}`} onClick={() => setImageIndexForPost(p.Id || p.id, idx)}>
											{f.type && f.type.startsWith('video/') ? (
												<video src={f.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
											) : (
												<img src={f.url} alt={`thumb-${idx}`} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
											)}
										</div>
									));
								})()}
							</div>

					</div>
				) : null}

				{/* show non-image files (PDF/TXT/etc) in a separate block */}
				{(() => {
					const nonImage = (p.files || []).filter(f => !(f.type && f.type.startsWith('image/')));
					if (!nonImage || nonImage.length === 0) return null;
					const fileEmoji = (f) => {
						const t = (f.type || '').toLowerCase();
						const name = (f.name || f.fileName || f.Nome || f.nome || '').toLowerCase();
						if (t.includes('pdf') || name.endsWith('.pdf')) return '📄';
						if (t.includes('text') || name.endsWith('.txt')) return '📝';
						if (name.endsWith('.csv')) return '📑';
						if (name.endsWith('.doc') || name.endsWith('.docx') || t.includes('word')) return '📄';
						return '📎';
					};
					return (
						<div className="files-preview" style={{ marginTop: 12 }}>
							<div className="files-row files-row-scrollable">
								{nonImage.map((f, idx) => {
									const lowerType = (f.type || '').toString().toLowerCase();
									const lowerName = (f.name || f.fileName || f.Nome || f.nome || '').toString().toLowerCase();
									const isPdf = lowerType.includes('pdf') || lowerName.endsWith('.pdf');
									// For PDFs: prefer NomeArquivo (column from backend). If missing, show blank (per request).
									const displayName = isPdf
										? (f.NomeArquivo || f.nome || f.Nome || f.fileName || f.name) || ''
										: (f.nome || f.Nome || f.fileName || f.name) || (f.type || '').split('/').pop() || `Arquivo ${idx+1}`;
									const downloadName = (f.NomeArquivo || f.nome || f.Nome || f.fileName || f.name) || (f.type || '').split('/').pop() || `Arquivo-${idx+1}`;
									return (
										<div key={f.id || f.url || idx} className="file-item">
											<div style={{ fontSize: 18 }}>{fileEmoji(f)}</div>
											<a className="file-title" href={f.url} onClick={async (e) => {
												try {
													// If this is a server endpoint for PDF, fetch and force a blob download (consistent with Orcamento flow)
													if (f.url && (f.url.startsWith('/api/PdfPostagem/') || (f.type && f.type.toLowerCase().includes('pdf')))) {
														e.preventDefault();
														const resp = await fetch(f.url, { method: 'GET', credentials: 'same-origin' });
														if (!resp.ok) throw new Error('Falha ao baixar arquivo: ' + resp.status);
														const blob = await resp.blob();
														const urlBlob = window.URL.createObjectURL(blob);
														const a = document.createElement('a');
														a.href = urlBlob;
														a.download = downloadName || (f.NomeArquivo || f.nome || f.name) || 'arquivo.pdf';
														document.body.appendChild(a);
														a.click();
														a.remove();
														window.URL.revokeObjectURL(urlBlob);
														return;
													}
												} catch (err) {
													console.warn('Erro ao baixar via fetch, abrindo diretamente', err);
												}
											}} target="_blank" rel="noopener noreferrer" download={downloadName} title={displayName}>
												{displayName}
											</a>
										</div>
									);
								})}
							</div>
						</div>
					);
				})()}
					</>
					)}

					{/* Confirmation modal for delete */}
					{confirmDeletePostId === (p.Id || p.id) && (
						<div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setConfirmDeletePostId(null)}>
							<div style={{ background: '#fff', borderRadius: 10, padding: '28px 32px', minWidth: 300, maxWidth: 420, boxShadow: '0 4px 32px rgba(0,0,0,0.18)', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
								<div style={{ fontWeight: 700, fontSize: 18, marginBottom: 10 }}>Excluir publicação</div>
								<div style={{ color: '#666', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>Essa ação não pode ser desfeita.</div>
								<div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
									<button className="button-create" style={{ background: '#9e9e9e', fontSize: 14, padding: '10px 20px', minWidth: 110 }} onClick={() => setConfirmDeletePostId(null)}>Cancelar</button>
									<button className="button-create" style={{ background: '#d32f2f', fontSize: 14, padding: '10px 20px', minWidth: 110 }} onClick={() => deletePost(p.Id || p.id)}>Excluir</button>
								</div>
							</div>
						</div>
					)}

				<div className="post-separator" />
					<div className="post-stats">
						<span>{(likesCountByPost[p.Id || p.id] || 0)} curtidas</span>
						<span className="stats-sep">•</span>
						<span role="button" tabIndex={0} aria-label="Abrir comentários" className="post-stats-comments" onClick={(e) => { toggleComments(p); try { e.currentTarget && e.currentTarget.blur(); } catch {} }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleComments(p); try { e.currentTarget && e.currentTarget.blur(); } catch {} } }}>{(commentsCountByPost[p.Id || p.id] || 0)} comentários</span>
					</div>
					<div className="post-actions">
						<button type="button" className={`action-btn like-btn ${likedPosts.has(p.Id || p.id) ? 'liked' : ''}`} onClick={() => toggleLike(p)} aria-pressed={likedPosts.has(p.Id || p.id)}>
							<svg className="icon heart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg">
								<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
							</svg>
							<span>Curtir</span>
						</button>
						<button type="button" className={`action-btn comments-btn ${commentsOpenPosts.has(p.Id || p.id) ? 'open' : ''}`} onClick={() => toggleComments(p)}>
							<svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" xmlns="http://www.w3.org/2000/svg"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
							<span>Comentários</span>
						</button>
					</div>
					{commentsOpenPosts.has(p.Id || p.id) && (
						<div className="comments-section">
							<div className="comment-input-row">
								<input className="comment-input" type="text" placeholder="Escreva um comentário..." value={commentInputs[p.Id || p.id] || ''} onChange={(e) => setCommentInputForPost(p.Id || p.id, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitCommentForPost(p.Id || p.id); } }} />
								<button type="button" className="comment-button" onClick={() => submitCommentForPost(p.Id || p.id)}>Comentar</button>
							</div>
							{(commentsByPost[p.Id || p.id] || []).length > 0 && (
								<div className="comment-list">
									{(commentsByPost[p.Id || p.id] || []).map(c => {
										// Buscar nome do usuário do comentário
										const commentUserId = c.UsuarioSistemaId || c.usuarioSistemaId || c.UsuarioSistema?.Id || c.usuarioSistema?.id;
										if (commentUserId && !userNamesById[commentUserId] && !userNamesFetchingRef.current.has(commentUserId)) {
											fetchUserName(commentUserId);
										}
										const commentUserName = getUserName(commentUserId) || 'Usuário';
										return (
										<div key={c.id || c.Id} className="comment-item">
											<div className="comment-header"><strong>{commentUserName}</strong><span className="comment-time">{c.DataHora ? formatPostDateMinutes(c.DataHora) : ''}</span></div>
											<div className="comment-text">{c.conteudo || c.text || c.Conteudo}</div>
										</div>
										);
									})}
								</div>
							)}
						</div>
					)}
				</div>
				);
				})}
				</div>
				<div ref={bottomRef} id="infinite-sentinel" style={{ height: 1 }} />
				<div style={{ textAlign: 'center', padding: 12, color: '#666' }}>
					{postsLoading ? 'Carregando publicações...' : (
						loadingMore ? (
							// spinner image (SVG) rotating 360deg in loop
							<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
								<svg width="28" height="28" viewBox="0 0 50 50" style={{ animation: 'spin 1s linear infinite' }}>
									<circle cx="25" cy="25" r="20" stroke="#ff8c00" strokeWidth="4" fill="none" strokeLinecap="round" strokeDasharray="31.4 31.4" />
								</svg>
								<span>Carregando mais publicações...</span>
							</div>
						) : (!hasMorePosts ? 'Não há mais publicações.' : '')
					)}
					<style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
				</div>
			</main>

			<aside className={`right-side ${overdue.length > 0 ? 'has-overdue' : ''}`}>
				{overdue.length > 0 && (
					<div className="title">Atrasos</div>
				)}
				{agendamentosLoading && <div style={{ marginTop: 8, color: '#666' }}>Carregando agendamentos...</div>}
				<div className="right-subcontainer">
					<div className="right-subcontainer-title">Notificações</div>
					<div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
						{!agendamentosLoading && overdue.length === 0 && (
							<div style={{ background: '#fff', padding: '8px 10px', borderRadius: 6, border: '1px solid #eee' }}>
								Nenhum agendamento em atraso
							</div>
						)}
						{!agendamentosLoading && overdue.length > 0 && overdue.map(a => (
							<div
								key={a.id || a.Id || a.PostagemId || (a._dateObj && a._dateObj.getTime())}
								className="agendamento-card"
								role="button"
								tabIndex={0}
								onClick={() => navigate('/sidebar/AgendamentosDropdown', { state: { filtroAtrasados: true, editAgendamentoId: (a.Id || a.id) } })}
								onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/sidebar/AgendamentosDropdown', { state: { filtroAtrasados: true, editAgendamentoId: (a.Id || a.id) } }); } }}
							>
								<div className="agendamento-card-nome">{getClienteNome(a)}</div>
								<div className="agendamento-card-data" style={{ color: 'red', fontWeight: 700 }}>{(a._dateObj || new Date(getAgendaDate(a) || a.dataPrevista || a.dataHora)).toLocaleDateString('pt-BR')}</div>
							</div>
						))}
							<button className="ver-todos-agendamentos" onClick={() => navigate('/sidebar/AgendamentosDropdown', { state: { filtroAtrasados: true } })} role="button">Ver Todos</button>
					</div>
				</div>
				<div style={{ color: '#777', fontSize: 13 }}>Lista de itens em atraso ou pendentes.</div>
				<div className="versao-sistema">
					<div>Versão do sistema</div>
					<div>{versaoSistema || 'Indisponível'}</div>
				</div>

			</aside>
		</div>
	);
}

export default Home;

// Lightbox render placed here to avoid cluttering JSX above
// Note: React will mount/unmount this based on `lightboxOpen` state
